import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { splitLocalizedName } from '@/lib/splitHostName';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { HostListItem } from '@/services/hostManagement/hostManagement.types';
import useHostMutation from '../../hooks/useHostMutation';
import { getColumns } from './Column';

export function HostManagementTable({
  data,
  isLoading,
  pagination,
  additionalValue,
}: {
  data: HostListItem[];
  isLoading: boolean;
  pagination: Pagination;
  additionalValue: additionalValue;
}) {
  const { mutate } = useHostMutation();
  const { openConfirmation } = useConfirmationStore();
  const { locale, t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns({ mutate, openConfirmation, t }), [mutate, openConfirmation, t]);
  const { data: options } = useDropdown(
    'clubName',
    additionalValue?.commonClubId,
    undefined,
    undefined,
    Boolean(additionalValue?.commonClubId),
  );
  const { japanese, english } = splitLocalizedName(options?.[0]?.label);
  const clubName = locale === 'en' ? english : japanese;

  const finalData = data?.map((item: HostListItem, index: number) => {
    return {
      SN: index + 1,
      Position: item.position,
      'Host Name': item.hostNameEng,
      Rank: item.rank,
      Status: item.status === 'A' ? 'Active' : 'Blocked',
      'Created Date': item.createdDate,
      'Updated Date': item.updatedDate,
    };
  });

  function handleExport() {
    exportCsvSingleFilePapa(finalData, {
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  }

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: clubName ? (
        <Translation id="storeManagement.hostManagement.page.table.title" values={{ clubName: clubName }} />
      ) : (
        ''
      ),
      loading: isLoading,
      totalPage: pagination.totalItems,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
